import React, { useState, useEffect, useRef } from 'react';

const EditableElementOverlay = ({ 
  isVisible = false, 
  targetElements = [],
  onElementMoved = null,
  onElementResized = null,
  onSaveDesign = null
}) => {
  const [draggedElement, setDraggedElement] = useState(null);
  const [resizedElement, setResizedElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  const [designChanges, setDesignChanges] = useState({});
  const [foundElements, setFoundElements] = useState([]);
  const overlayRef = useRef(null);

  // Find actual DOM elements and their positions
  useEffect(() => {
    if (!isVisible || targetElements.length === 0) {
      setFoundElements([]);
      return;
    }

    const findElements = () => {
      const elements = [];
      targetElements.forEach((target, index) => {
        let domElements = [];
        
        if (target.className) {
          domElements = document.querySelectorAll(`.${target.className.split(' ').join('.')}`);
        } else if (target.selector) {
          domElements = document.querySelectorAll(target.selector);
        }
        
        if (domElements.length > 0) {
          domElements.forEach((el, elIndex) => {
            const rect = el.getBoundingClientRect();
            elements.push({
              name: target.name + (domElements.length > 1 ? ` (${elIndex + 1})` : ''),
              element: el,
              rect: {
                x: rect.x,
                y: rect.y,
                width: rect.width,
                height: rect.height
              },
              targetIndex: index,
              elementIndex: elements.length
            });
          });
        }
      });
      
      setFoundElements(elements);
    };

    findElements();
    const interval = setInterval(findElements, 100); // Update positions every 100ms
    
    return () => clearInterval(interval);
  }, [isVisible, targetElements]);

  // Handle mouse down for dragging
  const handleMouseDown = (e, element, elementIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.target.getBoundingClientRect();
    setDraggedElement({ element, elementIndex });
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle mouse down for resizing
  const handleResizeMouseDown = (e, element, elementIndex, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    setResizedElement({ element, elementIndex });
    setResizeHandle(handle);
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (draggedElement) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Update the element position
      const elementId = `editable-${draggedElement.elementIndex}`;
      setDesignChanges(prev => ({
        ...prev,
        [elementId]: {
          ...prev[elementId],
          x: newX,
          y: newY
        }
      }));
    }
    
    if (resizedElement && resizeHandle) {
      const element = foundElements[resizedElement.elementIndex];
      if (element && element.element) {
        const rect = element.element.getBoundingClientRect();
        const newWidth = Math.max(50, e.clientX - rect.left);
        const newHeight = Math.max(30, e.clientY - rect.top);
        
        const elementId = `editable-${resizedElement.elementIndex}`;
        setDesignChanges(prev => ({
          ...prev,
          [elementId]: {
            ...prev[elementId],
            width: newWidth,
            height: newHeight
          }
        }));
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (draggedElement && onElementMoved) {
      const elementId = `editable-${draggedElement.elementIndex}`;
      const changes = designChanges[elementId];
      if (changes) {
        onElementMoved(draggedElement.elementIndex, changes.x, changes.y);
      }
    }
    
    if (resizedElement && onElementResized) {
      const elementId = `editable-${resizedElement.elementIndex}`;
      const changes = designChanges[elementId];
      if (changes) {
        onElementResized(resizedElement.elementIndex, changes.width, changes.height);
      }
    }
    
    setDraggedElement(null);
    setResizedElement(null);
    setResizeHandle(null);
  };

  // Add event listeners
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isVisible, draggedElement, resizedElement, dragOffset, resizeHandle, designChanges]);

  if (!isVisible || foundElements.length === 0) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 10003
      }}
    >
      {foundElements.map((element, index) => {
        const elementId = `editable-${index}`;
        const changes = designChanges[elementId] || {};
        const isDragging = draggedElement?.elementIndex === index;
        const isResizing = resizedElement?.elementIndex === index;
        
        // Get element position and size from the found element
        const currentX = changes.x !== undefined ? changes.x : element.rect.x;
        const currentY = changes.y !== undefined ? changes.y : element.rect.y;
        const currentWidth = changes.width !== undefined ? changes.width : element.rect.width;
        const currentHeight = changes.height !== undefined ? changes.height : element.rect.height;
        
        return (
          <div
            key={elementId}
            style={{
              position: 'absolute',
              left: currentX,
              top: currentY,
              width: currentWidth,
              height: currentHeight,
              border: '3px solid #ff00ff', // Magenta for editable elements
              backgroundColor: 'rgba(255, 0, 255, 0.1)',
              pointerEvents: 'auto',
              cursor: isDragging ? 'grabbing' : 'grab',
              zIndex: isDragging || isResizing ? 10004 : 10003,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '4px',
              boxSizing: 'border-box'
            }}
            onMouseDown={(e) => handleMouseDown(e, element, index)}
          >
            {/* Element label */}
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#ff00ff',
              padding: '2px 6px',
              fontSize: '10px',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              marginBottom: '4px',
              border: '1px solid #ff00ff'
            }}>
              {element.name}
            </div>
            
            {/* Resize handles */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '12px',
                height: '12px',
                backgroundColor: '#ff00ff',
                cursor: 'nw-resize',
                border: '1px solid #fff'
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, element, index, 'se')}
            />
            
            {/* Position indicator */}
            <div style={{
              position: 'absolute',
              top: '-20px',
              left: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#ff00ff',
              padding: '2px 4px',
              fontSize: '8px',
              borderRadius: '2px',
              whiteSpace: 'nowrap'
            }}>
              {Math.round(currentX)}, {Math.round(currentY)}
            </div>
          </div>
        );
      })}
      
      {/* Save Design Button */}
      <div
        style={{
          position: 'fixed',
          top: 10,
          right: 10,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '10px',
          borderRadius: '8px',
          border: '2px solid #ff00ff',
          zIndex: 10005,
          pointerEvents: 'auto'
        }}
      >
        <div style={{ color: '#ff00ff', fontWeight: 'bold', marginBottom: '10px' }}>
          🎨 Design Mode
        </div>
        <button
          onClick={() => onSaveDesign && onSaveDesign(designChanges)}
          style={{
            backgroundColor: '#ff00ff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            marginRight: '8px'
          }}
        >
          Save Design
        </button>
        <button
          onClick={() => setDesignChanges({})}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Reset
        </button>
        <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
          Drag to move, resize from corner
        </div>
      </div>
    </div>
  );
};

export default EditableElementOverlay;
